/**
 * Profile helpers for the V2 dashboard.
 *
 * Mirrors the legacy form structure:
 *  - "Datos básicos" is always the first section (order = 0). It contains
 *    the static fields (name, firstLastname, email, company, phone) plus
 *    any RegistrationField whose `section` is null (i.e. unassigned).
 *  - The remaining RegistrationFields are grouped by their FormSection
 *    (one section per FormSection, ordered by `order`).
 *  - Each section renders its fields in `order` ascending.
 *
 * Visibility rules (kept consistent with MemberForm.vue):
 *  - HEAD_OFFICE users see all fields regardless of visibleFor.
 *  - For other roles, fields with non-empty `visibleFor` are filtered by
 *    the user's role.
 *  - Fields with a `parentField` are kept but flagged with `_parentId`
 *    so the ProfileField can apply conditional visibility at render time.
 *  - `allowMultiple` on the parent: one rendered copy of the child per
 *    selected parent value, populated from `conditionalOptions[parentValue]`.
 */

const ROLE_HEAD_OFFICE = "HEAD_OFFICE";

const isHeadOfficeUser = (userRole) => userRole === ROLE_HEAD_OFFICE;

const STATIC_FIELDS = [
  { key: "name", label: "Nombres", type: "text", required: true, editable: true },
  { key: "firstLastname", label: "Apellidos", type: "text", required: true, editable: true },
  { key: "email", label: "Correo electrónico", type: "email", required: true, editable: false },
  { key: "company", label: "Empresa", type: "text", required: false, editable: true },
  { key: "phone", label: "Teléfono", type: "tel", required: false, editable: true },
];

const mapStaticValue = (key, user) => {
  if (!user) return "";
  if (key === "name") return user.name || "";
  if (key === "firstLastname") return user.firstLastname || "";
  if (key === "email") return user.email || "";
  if (key === "company") return user.company || "";
  if (key === "phone") return user.phone || "";
  return "";
};

export const getStaticFields = (user) =>
  STATIC_FIELDS.map((f) => ({
    ...f,
    value: mapStaticValue(f.key, user),
    isStatic: true,
  }));

const detectFieldType = (field) => {
  switch (field.fieldType) {
    case "DATE":
      return "date";
    case "LIST":
      return "list";
    case "NUMBER":
      return "number";
    case "TEXT":
    default:
      return field.accept && field.accept.length ? "text" : "text";
  }
};

const fieldIsVisibleForRole = (field, userRole) => {
  if (isHeadOfficeUser(userRole)) return true;
  if (!field.visibleFor || !field.visibleFor.length) return true;
  return field.visibleFor.includes(userRole);
};

const findById = (fields, id) => {
  if (!id) return null;
  const target = String(id);
  return fields.find((f) => String(f._id) === target) || null;
};

const resolveParentValue = (parent) => {
  if (!parent) return [];
  if (Array.isArray(parent.value)) return parent.value.filter((v) => v !== null && v !== undefined && v !== "");
  if (parent.value === null || parent.value === undefined || parent.value === "") return [];
  return [parent.value];
};

const expandConditionalChild = (field) => {
  // For a child field, the parent must be fetched externally. The caller
  // passes the parent via `parentField` on the field. We don't have the
  // parent here, so this is a no-op placeholder; the real expansion
  // happens in buildProfileSections where we walk fields with their
  // resolved parent values.
  return [field];
};

const applyRoleVisibility = (fields, userRole) =>
  fields.filter((f) => fieldIsVisibleForRole(f, userRole));

const decorateDynamic = (field, sectionId, memberValue) => {
  const decorated = {
    ...field,
    isStatic: false,
    sectionId: sectionId || field.section || null,
    type: detectFieldType(field),
    required: Boolean(field.required),
    editable: true,
    options: Array.isArray(field.options) ? field.options : [],
    parentId: field.parentField
      ? typeof field.parentField === "object"
        ? String(field.parentField._id || "")
        : String(field.parentField)
      : null,
    allowMultiple: Boolean(field.allowMultiple),
    conditionalOptions:
      field.conditionalOptions instanceof Map
        ? Object.fromEntries(field.conditionalOptions)
        : field.conditionalOptions || null,
  };
  // The value coming from GET /registrationFields/form/:orgId is the
  // template's default — not the user's current value. The user's actual
  // value lives in user.memberData[formattedName].value, exactly like
  // MemberForm.vue:1637-1649 does in the legacy form. If the user has an
  // entry for this field (even with an empty value), use it; otherwise
  // keep the template's default. This makes the form display what is
  // actually persisted and makes save round-trip the same values.
  if (memberValue !== undefined) {
    decorated.value = memberValue;
  }
  return decorated;
};

const sortByOrderThenName = (a, b) => {
  const ao = a.order === null || a.order === undefined ? 9999 : a.order;
  const bo = b.order === null || b.order === undefined ? 9999 : b.order;
  if (ao !== bo) return ao - bo;
  const an = a.name || a.label || a.formattedName || "";
  const bn = b.name || b.label || b.formattedName || "";
  return an.localeCompare(bn);
};

/**
 * Order a list of fields exactly like the legacy MemberForm.vue:1671-1687:
 *  1. Fields without `order` come first.
 *  2. Fields with `order` are spliced in at position `order - 1` in
 *     ascending `order`, pushing unordered fields to the back.
 *
 * This preserves gaps in the `order` sequence (e.g. 1, 3, 5 → positions
 * 0, 2, 4) which a plain sort would collapse, and keeps the relative
 * order of unordered fields as they appear in the input.
 */
export const orderFieldsLikeLegacy = (fields) => {
  if (!Array.isArray(fields)) return [];
  const unordered = fields.filter(
    (f) => f && (f.order === null || f.order === undefined),
  );
  const ordered = fields
    .filter((f) => f && f.order !== null && f.order !== undefined)
    .slice()
    .sort((a, b) => a.order - b.order);
  const out = [...unordered];
  ordered.forEach((field) => {
    const position = Math.max(0, Number(field.order) - 1);
    if (position >= out.length) {
      out.push(field);
    } else {
      out.splice(position, 0, field);
    }
  });
  return out;
};

/**
 * Resolve a registration field's value.
 * Handles multi-select (array), object (for parents with allowMultiple
 * children), and primitive values.
 */
export const resolveFieldValue = (field) => {
  if (!field) return null;
  const v = field.value;
  if (v === null || v === undefined || v === "" || v === "N/D") return null;
  return v;
};

/**
 * Evaluate whether a child field is visible given the full set of
 * resolved field values. Mirrors the legacy isFieldVisible logic.
 */
export const isFieldVisible = (field, allFields) => {
  if (!field) return false;
  if (!field.parentId) return true;
  const parent = findById(allFields, field.parentId);
  if (!parent) return false;
  if (parent.allowMultiple) {
    const values = resolveParentValue(parent);
    if (!values.length) return false;
    if (field.conditionalOptions) {
      return values.some((v) => {
        const opts = field.conditionalOptions[String(v)];
        return Array.isArray(opts) && opts.length > 0;
      });
    }
    return true;
  }
  return resolveParentValue(parent).length > 0;
};

/**
 * Expand a child field according to its parent's allowMultiple state.
 * Returns an array of field "instances" (one per selected parent value).
 * If the parent is not allowMultiple, returns [field] as-is.
 */
export const expandFieldInstances = (field, allFields) => {
  if (!field.parentId) return [field];
  const parent = findById(allFields, field.parentId);
  if (!parent) return [field];
  if (!parent.allowMultiple) {
    return resolveParentValue(parent).length ? [field] : [];
  }
  const values = resolveParentValue(parent);
  if (!values.length) return [];
  if (!field.conditionalOptions) return [];
  const out = [];
  values.forEach((pv) => {
    const opts = field.conditionalOptions[String(pv)];
    if (Array.isArray(opts) && opts.length > 0) {
      const instance = { ...field };
      if (
        field.value &&
        typeof field.value === "object" &&
        !Array.isArray(field.value)
      ) {
        instance.value = field.value[String(pv)] ?? null;
      } else {
        instance.value = null;
      }
      instance._instanceKey = String(pv);
      instance._instanceLabel = String(pv);
      instance._dynamicOptions = opts;
      out.push(instance);
    }
  });
  return out;
};

const BASIC_SECTION_ID = "basic";
const BASIC_SECTION_NAME = "Datos básicos";
const BASIC_SECTION_ORDER = -1;

/**
 * Build the full section tree to render in the Profile view.
 *
 * @param {Object} params
 * @param {Object} params.user - The authenticated user (Vuex state.user)
 * @param {Array}  params.registrationFields - RegistrationField[] for the
 *   head office (already loaded by the caller)
 * @param {Array}  params.formSections - FormSection[] for the head office
 * @returns {Array<{id, name, order, fields}>}
 */
export const buildProfileSections = ({ user, registrationFields, formSections }) => {
  const userRole = user && user.role;
  // Order FormSections using the same 1-based splice logic the legacy
  // applies to fields, so a section with order=1 renders first,
  // order=2 second, etc. Sections without order are appended.
  const sections = orderFieldsLikeLegacy(formSections || []);

  // Index the user's memberData by formattedName so we can overlay the
  // current persisted value on top of the registration field template
  // (mirrors MemberForm.vue:1637-1649 in the legacy form).
  const memberDataByFormattedName = {};
  if (user && Array.isArray(user.memberData)) {
    user.memberData.forEach((entry) => {
      if (entry && entry.formattedName) {
        memberDataByFormattedName[String(entry.formattedName)] = entry.value;
      }
    });
  }

  // Dynamic fields with the role filter applied
  const visibleDynamic = applyRoleVisibility(registrationFields || [], userRole);

  // Partition dynamic fields by section
  const unassigned = [];
  const grouped = {};
  sections.forEach((s) => {
    grouped[String(s._id)] = [];
  });
  visibleDynamic.forEach((field) => {
    const sectionId = field.section ? String(field.section._id || field.section) : null;
    if (sectionId && grouped[sectionId]) {
      grouped[sectionId].push(field);
    } else {
      unassigned.push(field);
    }
  });

  // Build section list
  const result = [];

  // 1) Datos básicos: static fields are always rendered first (legacy
  // basicInfo), then the unassigned dynamic fields ordered using the
  // legacy splice logic.
  const basicDynamic = orderFieldsLikeLegacy(unassigned).map((f) =>
    decorateDynamic(f, null, memberDataByFormattedName[String(f.formattedName)])
  );
  const basicFields = [...getStaticFields(user), ...basicDynamic];
  result.push({
    id: BASIC_SECTION_ID,
    name: BASIC_SECTION_NAME,
    order: BASIC_SECTION_ORDER,
    fields: basicFields,
  });

  // 2) Form sections (already in legacy order)
  sections.forEach((s) => {
    const fields = orderFieldsLikeLegacy(grouped[String(s._id)] || []).map((f) =>
      decorateDynamic(
        f,
        String(s._id),
        memberDataByFormattedName[String(f.formattedName)]
      )
    );
    if (!fields.length) return;
    result.push({
      id: String(s._id),
      name: s.name || "Sección",
      order: s.order === null || s.order === undefined ? 0 : s.order,
      fields,
    });
  });

  return result;
};

/**
 * Diff two section lists (old vs new) returning the field paths whose
 * value changed. Used after a save to update local state.
 */
export const diffFieldValues = (oldSections, newSections, valueByKey) => {
  const changes = [];
  oldSections.forEach((oldSection) => {
    const newSection = newSections.find((s) => s.id === oldSection.id);
    if (!newSection) return;
    oldSection.fields.forEach((oldField) => {
      if (!oldField.isStatic) return;
      const newField = newSection.fields.find(
        (f) => f.isStatic && f.key === oldField.key
      );
      if (!newField) return;
      if ((oldField.value || "") !== (valueByKey[oldField.key] || "")) {
        changes.push({ key: oldField.key, value: valueByKey[oldField.key] });
      }
    });
  });
  return changes;
};

export const isStaticKey = (key) =>
  STATIC_FIELDS.some((f) => f.key === key);

export const STATIC_FIELD_KEYS = STATIC_FIELDS.map((f) => f.key);
